import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Card,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import ToggleButton from "@material-ui/lab/ToggleButton";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import ThermalSalesReportIMIN from "./ThermalSalesReportIMIN";
import ThermalSalesReport from "./ThermalSalesReport";
import * as api from "../../api";
import { useEffectiveProfile } from "../../utils/useEffectiveProfile";

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    zIndex: 13000,
  },
  dialogPaper: {
    width: "32%",
    maxWidth: 420,
    maxHeight: "calc(100vh - 100px)",
    marginTop: theme.spacing(2),
    marginBottom: 25,
    borderRadius: 12,
    [theme.breakpoints.down("sm")]: {
      width: "60%",
    },
    [theme.breakpoints.down("xs")]: {
      width: "94%",
      maxHeight: "calc(100vh - 32px)",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  dialogTitle: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& h2": { fontSize: "0.9375rem", fontWeight: 600 },
  },
  dialogContent: {
    padding: theme.spacing(1, 1.5),
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  controls: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.75),
    "& > *": { margin: 0 },
  },
  controlRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  datePicker: {
    minWidth: 140,
    "& .MuiInputBase-root": { fontSize: "0.8125rem" },
  },
  previewCard: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(0.75),
    flex: "1 1 auto",
    minHeight: 120,
    maxHeight: "58vh",
    overflowY: "auto",
    borderRadius: 8,
    boxShadow: theme.shadows[1],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
}));

const DSRDialog = ({
  open,
  onClose,
  date,
  handleDateChange,
  handleSubmit,
  showPreview,
  rows,
  user,
  classes: parentClasses,
}) => {
  const classes = useStyles();
  const effectiveProfile = useEffectiveProfile();
  const shiftTrackingEnabled = !!effectiveProfile?.enableShiftTracking;
  const [reportType, setReportType] = useState("consolidated");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (open && reportType === "employee-wise") {
      api.fetchEmployees().then((res) => setEmployees(res.data || [])).catch(() => setEmployees([]));
    }
    if (open && reportType === "user-wise") {
      api.fetchUsers().then((res) => setUsers(res.data || [])).catch(() => setUsers([]));
    }
  }, [open, reportType]);

  // Shift tracking could be switched off elsewhere while this dialog already
  // has "Employee-wise" selected - fall back to Consolidated instead of
  // showing a filter mode that no longer has a menu option.
  useEffect(() => {
    if (!shiftTrackingEnabled && reportType === "employee-wise") {
      setReportType("consolidated");
      setSelectedEmployeeId("");
    }
  }, [shiftTrackingEnabled, reportType]);

  const isWindows = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes("windows");
  };

  // `creator` is stored as an array of ids on the invoice.
  const invoiceCreatorId = (inv) =>
    Array.isArray(inv.creator) ? inv.creator[0] : inv.creator;

  // Super Admin is a platform-level account, not a salesperson - it has no
  // place in a per-user sales breakdown.
  const isSuperAdminUser = (u) =>
    Array.isArray(u.userType)
      ? u.userType.includes("superAdmin")
      : u.userType === "superAdmin";
  const selectableUsers = users.filter((u) => !isSuperAdminUser(u));

  const reportRows = (() => {
    if (reportType === "employee-wise" && selectedEmployeeId) {
      return (rows || []).filter(
        (inv) =>
          inv.employeeId === selectedEmployeeId ||
          (inv.employeeName &&
            inv.employeeName ===
              (employees.find((e) => e._id === selectedEmployeeId)?.name || ""))
      );
    }
    if (reportType === "user-wise" && selectedUserId) {
      return (rows || []).filter((inv) => invoiceCreatorId(inv) === selectedUserId);
    }
    // No one picked: show every row so the breakdown covers all users/employees.
    return rows || [];
  })();

  const selectedUserName =
    (reportType === "user-wise" &&
      selectedUserId &&
      (users.find((u) => u._id === selectedUserId)?.name ||
        users.find((u) => u._id === selectedUserId)?.email)) ||
    null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ root: classes.dialogRoot, paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="h6" style={{ fontSize: "0.9375rem", fontWeight: 600 }}>
          Daily Sales Report
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className={`${classes.dialogContent} slim-scrollbar`}>
        <div className={classes.controls}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Box className={classes.controlRow}>
              <KeyboardDatePicker
                margin="dense"
                label="Select Date"
                format="MMM dd, yyyy"
                value={date}
                onChange={handleDateChange}
                className={classes.datePicker}
                inputVariant="outlined"
                size="small"
              />
              <Button variant="contained" color="primary" onClick={handleSubmit} size="small" style={{ minHeight: 40 }}>
                Search Report
              </Button>
            </Box>
          </MuiPickersUtilsProvider>

          <Box className={classes.controlRow} style={{ alignItems: "center" }}>
            <Typography variant="caption" color="textSecondary" style={{ marginRight: 8 }}>
              Report type
            </Typography>
            <ToggleButtonGroup
              value={reportType}
              exclusive
              onChange={(_, v) => {
                if (v != null) {
                  setReportType(v);
                  // Clear the other mode's filter so a stale selection can't
                  // silently narrow the next report.
                  if (v !== "employee-wise") setSelectedEmployeeId("");
                  if (v !== "user-wise") setSelectedUserId("");
                }
              }}
              size="small"
            >
              <ToggleButton value="consolidated">Consolidated</ToggleButton>
              {shiftTrackingEnabled && <ToggleButton value="employee-wise">Employee-wise</ToggleButton>}
              <ToggleButton value="user-wise">User-wise</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {reportType === "employee-wise" && (
            <FormControl variant="outlined" size="small" className={classes.datePicker} style={{ minWidth: 180 }}>
              <InputLabel id="dsr-employee-label">Select employee</InputLabel>
              <Select
                labelId="dsr-employee-label"
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                label="Select employee"
              >
                <MenuItem value="">
                  <em>All employees</em>
                </MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp._id} value={emp._id}>
                    {emp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {reportType === "user-wise" && (
            <FormControl variant="outlined" size="small" className={classes.datePicker} style={{ minWidth: 180 }}>
              <InputLabel id="dsr-user-label">Select user</InputLabel>
              <Select
                labelId="dsr-user-label"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                label="Select user"
              >
                <MenuItem value="">
                  <em>All users</em>
                </MenuItem>
                {selectableUsers.map((u) => (
                  <MenuItem key={u._id} value={u._id}>
                    {u.name || u.email}
                    {u.userType ? ` — ${Array.isArray(u.userType) ? u.userType.join(", ") : u.userType}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>

        {showPreview && (
          <Card className={`${classes.previewCard} slim-scrollbar`}>
            {isWindows() ? (
              <ThermalSalesReport
                userProfile={user.userProfile}
                salesData={{
                  invoices: reportRows,
                  isPrintReport: true,
                }}
                selectedDate={date}
                reportType={reportType}
                selectedEmployeeId={reportType === "employee-wise" ? selectedEmployeeId : null}
                selectedEmployeeName={
                  reportType === "employee-wise" && selectedEmployeeId
                    ? employees.find((e) => e._id === selectedEmployeeId)?.name || null
                    : null
                }
                users={users}
                selectedUserName={selectedUserName}
              />
            ) : (
              <ThermalSalesReportIMIN
                userProfile={user.userProfile}
                salesData={{
                  invoices: reportRows,
                  isPrintReport: true,
                }}
                selectedDate={date}
                reportType={reportType}
                selectedEmployeeId={reportType === "employee-wise" ? selectedEmployeeId : null}
                selectedEmployeeName={
                  reportType === "employee-wise" && selectedEmployeeId
                    ? employees.find((e) => e._id === selectedEmployeeId)?.name || null
                    : null
                }
                users={users}
                selectedUserName={selectedUserName}
              />
            )}
          </Card>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DSRDialog;
